import { nextTick } from '@vue/composition-api';
import boardEntryApi from '@/apis/board_entry';
import { BoardEntryConv } from '@/models/settouMessageBoard';

interface ViewTrackingOptions {
  getBoardEntries: () => BoardEntryConv[];
  getPaginatedBoardEntries: () => BoardEntryConv[];
  getItemsPerPage: () => number;
  getCurrentBoardMasterId: () => number;
  setCurrentPage: (page: number) => void;
  setCurrentBoardMasterId: (boardMasterId: number) => void;
}

interface BoardEntryViewTracking {
  setupEntryObserver: () => void;
  disconnectObserver: () => void;
  applyNotificationQuery: (boardMasterIdParam: string | null, latestEntryIdParam: string | null) => void;
  armMarkNewest: (boardMasterId: number) => void;
  runPendingViewAction: () => Promise<void>;
}

interface EntrySummary {
  id: number;
  boardMasterId: number;
  createdAt: string;
}

const SCROLL_DEBOUNCE_MS = 300;
const ENTRY_DOM_ID_PREFIX = 'board-entry-';

// 掲示板投稿の閲覧追跡（最終閲覧時刻の記録）を担う composable。
// IntersectionObserver で画面最上部の投稿を追跡し、スクロール／通知クリックに応じて
// 閲覧マーカー（entry_id）をサーバへ送る。期間・未読判定はサーバ側が created_at で行う。
export function useBoardEntryViewTracking(options: ViewTrackingOptions): BoardEntryViewTracking {
  let observer: IntersectionObserver | null = null;
  const visibleEntryIds = new Set<string>();
  let topmostVisibleEntry: EntrySummary | null = null;
  let scrollDebounceTimer: number | null = null;
  let scrollHandler: ((event: Event) => void) | null = null;
  // 掲示板ごとの、この画面でサーバへ送信済みの最終閲覧日時（の最大値、ISO8601）。
  const lastSubmittedAtByBoard = new Map<number, string>();
  // setupEntryObserver 呼び出し時に構築する「DOM id → {表示順, entry id, 掲示板id, 作成日時}」マップ（O(1) 検索用）。
  let paginatedEntryByDomId: Map<string, EntrySummary & { order: number }> = new Map();

  const isNewerThanSubmitted = (boardMasterId: number, createdAt: string): boolean => {
    const lastSubmittedAt = lastSubmittedAtByBoard.get(boardMasterId);
    if (lastSubmittedAt === undefined) return true;
    return new Date(createdAt).getTime() > new Date(lastSubmittedAt).getTime();
  };

  // entryId をサーバへ送り、サーバが解決した最終閲覧日時(created_at)を保存する。
  const updateLastViewedAt = async(boardMasterId: number, entryId: number, createdAt: string): Promise<void> => {
    if (!isNewerThanSubmitted(boardMasterId, createdAt)) return;
    try {
      const res = await boardEntryApi.updateLastViewedAt(boardMasterId, entryId);
      // サーバーは巻き戻り防止のため保存済み最大日時を返す。マルチタブ環境では
      // 送信値より新しい値が返ることがあるため、サーバーの値で上書きする。
      lastSubmittedAtByBoard.set(boardMasterId, res.data.last_viewed_at);
    } catch (e) {
      // 最終閲覧時刻の更新失敗は通知精度に影響するが非クリティカルのため無視する
    }
  };

  const recordTopmostVisibleAsViewed = async(): Promise<void> => {
    if (topmostVisibleEntry === null) return;
    const currentBoardMasterId = options.getCurrentBoardMasterId();
    // タブ切替直後（切替先の投稿取得前）は前の板の投稿が残っているため送信しない（サーバ側で 422 になる）。
    if (topmostVisibleEntry.boardMasterId !== currentBoardMasterId) return;

    await updateLastViewedAt(currentBoardMasterId, topmostVisibleEntry.id, topmostVisibleEntry.createdAt);
  };

  const onScroll = (event: Event): void => {
    // モーダル内などの内部スクロールは無視し、ページ全体のスクロールのみ対象とする
    if (event.target !== document) return;

    if (scrollDebounceTimer !== null) { window.clearTimeout(scrollDebounceTimer); }
    scrollDebounceTimer = window.setTimeout(() => { recordTopmostVisibleAsViewed(); }, SCROLL_DEBOUNCE_MS);
  };

  // setupEntryObserver は document.querySelectorAll で監視対象を取得するため、
  // 必ず DOM 反映後（await nextTick 後）に呼ぶこと。
  const setupEntryObserver = (): void => {
    if (observer !== null) { observer.disconnect(); }
    visibleEntryIds.clear();

    paginatedEntryByDomId = new Map(
      options.getPaginatedBoardEntries().map((entry, idx) => [
        `${ENTRY_DOM_ID_PREFIX}${entry.id}`,
        { order: idx, id: entry.id, boardMasterId: entry.board_master_id, createdAt: entry.created_at },
      ]),
    );

    const newObserver = new IntersectionObserver(
      changedEntries => {
        changedEntries.forEach(changed => {
          if (changed.isIntersecting) {
            visibleEntryIds.add(changed.target.id);
          } else {
            visibleEntryIds.delete(changed.target.id);
          }
        });
        // 表示中の行のうち最上位（表示順）の投稿を paginatedEntryByDomId で O(1) 解決する。
        let topmostOrder = Infinity;
        let topmost: EntrySummary | undefined;
        visibleEntryIds.forEach(domId => {
          const item = paginatedEntryByDomId.get(domId);
          if (item !== undefined && item.order < topmostOrder) {
            topmostOrder = item.order;
            topmost = { id: item.id, boardMasterId: item.boardMasterId, createdAt: item.createdAt };
          }
        });
        if (topmost !== undefined) {
          topmostVisibleEntry = topmost;
        }
      },
      { threshold: 0 },
    );
    observer = newObserver;
    document.querySelectorAll(`[id^="${ENTRY_DOM_ID_PREFIX}"]`).forEach(el => newObserver.observe(el));

    if (scrollHandler === null) {
      scrollHandler = onScroll;
      window.addEventListener('scroll', scrollHandler, true);
    }
  };

  const disconnectObserver = (): void => {
    if (observer !== null) {
      observer.disconnect();
      observer = null;
    }
    if (scrollHandler !== null) {
      window.removeEventListener('scroll', scrollHandler, true);
      scrollHandler = null;
    }
    if (scrollDebounceTimer !== null) {
      window.clearTimeout(scrollDebounceTimer);
      scrollDebounceTimer = null;
    }
  };

  // 通知クリックで渡された投稿（通知カードに表示されている最新投稿）までスクロールし、その id を閲覧マーカーとする。
  // updateLastViewedAt は日時の大小を比較して巻き戻りを防ぎつつ保存するため、この投稿以前の未読はすべて既読になる。
  // 取得範囲外で未表示の場合は false を返す（次回取得まで pending を持ち越すため）。
  const scrollToNotifiedEntry = async(boardMasterId: number, entryId: number): Promise<boolean> => {
    const allEntries = options.getBoardEntries();
    const entryIndexById = new Map(allEntries.map((e, i) => [e.id, i]));
    const idx = entryIndexById.get(entryId) ?? -1;
    if (idx === -1) return false; // 取得範囲外（フィルタ等で未表示）なら何もしない

    const matchedEntry = allEntries[idx];
    const page = Math.floor(idx / options.getItemsPerPage()) + 1;
    options.setCurrentPage(page);
    await nextTick(); // ページ切替後の再描画を待つ
    setupEntryObserver();

    const el = document.getElementById(`${ENTRY_DOM_ID_PREFIX}${entryId}`);
    if (el === null) return false;

    el.scrollIntoView({ block: 'start' });

    await updateLastViewedAt(boardMasterId, entryId, matchedEntry.created_at);
    return true;
  };

  // 初回データ取得時に一度だけ実行する閲覧マーカー処理の保留状態。
  let pendingBoardMasterId: number | null = null;
  let pendingScrollEntryId: number | null = null;
  let pendingMarkNewest = false;

  const markNewestAsViewed = (): void => {
    const entries = options.getBoardEntries();
    if (entries.length === 0) return;
    // 最新投稿は id 最大で判定する（オートインクリメントは作成順に単調増加するため作成日時と整合）。
    const newest = entries.reduce((a, b) => (a.id >= b.id ? a : b));
    updateLastViewedAt(options.getCurrentBoardMasterId(), newest.id, newest.created_at);
  };

  // タブ切替時など、切替先掲示板の最新投稿を閲覧マーカーとして次回取得時に記録する。
  const armMarkNewest = (boardMasterId: number): void => {
    pendingBoardMasterId = boardMasterId;
    pendingScrollEntryId = null;
    pendingMarkNewest = true;
  };

  // 未読通知クリック由来のクエリ（board_master_id / latest_entry_id）を反映する。
  const applyNotificationQuery = (boardMasterIdParam: string | null, latestEntryIdParam: string | null): void => {
    if (boardMasterIdParam !== null) {
      const boardMasterId = parseInt(boardMasterIdParam, 10);
      if (!Number.isNaN(boardMasterId)) {
        pendingBoardMasterId = boardMasterId;
        options.setCurrentBoardMasterId(boardMasterId); // SearchPane が currentTabId を watch して検索を再発火する
      }
    }

    if (latestEntryIdParam !== null) {
      const latestEntryId = parseInt(latestEntryIdParam, 10);
      if (!Number.isNaN(latestEntryId)) {
        pendingScrollEntryId = latestEntryId;
        return;
      }
    }
    // 通常開封時は取得後に最新投稿を閲覧マーカーとする。
    pendingMarkNewest = true;
  };

  const runPendingViewAction = async(): Promise<void> => {
    if (pendingBoardMasterId !== null && options.getCurrentBoardMasterId() !== pendingBoardMasterId) return;

    if (pendingScrollEntryId !== null) {
      const entryId = pendingScrollEntryId;
      const scrolled = await scrollToNotifiedEntry(options.getCurrentBoardMasterId(), entryId);
      if (!scrolled) return; // 対象掲示板のデータがまだ → 次回の取得まで持ち越す

      pendingScrollEntryId = null;
      pendingBoardMasterId = null;
      return;
    }
    if (pendingMarkNewest) {
      pendingMarkNewest = false;
      pendingBoardMasterId = null;
      markNewestAsViewed();
    }
  };

  return {
    setupEntryObserver,
    disconnectObserver,
    applyNotificationQuery,
    armMarkNewest,
    runPendingViewAction,
  };
}
